import React, { useCallback, useMemo } from 'react'

import { Form, notification } from 'antd'
import { Button, Flex, Header, Switch, Text } from 'govwell-ui'
import { ThumbnailFileFragment } from 'types/graphql'

import { FileUpload } from 'src/components/FileUpload/FileUpload'
import { AcceptedImages } from 'src/constants'
import { useUpdateOrganizationMutation } from 'src/fetch/organizations'
import { useFileUploadState } from 'src/hooks/use-file-upload-state'
import {
  useIsGovWellStaff,
  useMyOrg,
  useRefetchAppState,
} from 'src/layouts/AppStateContextLayout/utils'

import IconPreviews from './IconPreviews'

const CustomizationTab = () => {
  const [form] = Form.useForm()
  const organization = useMyOrg()
  const isGovWellStaff = useIsGovWellStaff()
  const refetchAppState = useRefetchAppState()
  const { mutateAsync: updateOrganization } = useUpdateOrganizationMutation()

  const handleIconUploaded = useCallback(
    async (newFile: ThumbnailFileFragment, reset: () => void) => {
      if (!organization?.id) {
        return
      }
      try {
        await updateOrganization({
          input: { aiChatIconFileId: newFile.id, organizationId: organization.id },
        })
        await refetchAppState?.()
      } catch {
        notification.error({ message: 'Failed to upload icon' })
      } finally {
        reset()
      }
    },
    [organization?.id, updateOrganization, refetchAppState]
  )

  const handleRemoveIcon = useCallback(async () => {
    if (!organization?.id) {
      return
    }
    try {
      await updateOrganization({
        input: { aiChatIconFileId: null, organizationId: organization.id },
      })
      await refetchAppState?.()
    } catch {
      notification.error({ message: 'Failed to remove icon' })
    }
  }, [organization?.id, updateOrganization, refetchAppState])

  const handleToggleCustomization = useCallback(
    async (enabled: boolean) => {
      if (!organization?.id) {
        return
      }
      try {
        await updateOrganization({
          input: { aiChatIconCustomizationEnabled: enabled, organizationId: organization.id },
        })
        await refetchAppState?.()
      } catch {
        notification.error({ message: 'Failed to update setting' })
      }
    },
    [organization?.id, updateOrganization, refetchAppState]
  )

  const accept = AcceptedImages
  const fileUploadProps = useFileUploadState({
    inputBucket: organization?.uuid ?? '',
    afterUpload: handleIconUploaded,
    accept,
  })

  const hasIcon = useMemo(() => !!organization?.aiChatIconUrl, [organization?.aiChatIconUrl])

  if (!organization) {
    return null
  }

  return (
    <Flex direction="column" gap={6} align="start">
      {isGovWellStaff && (
        <Switch
          value={organization.aiChatIconCustomizationEnabled}
          onValueChange={handleToggleCustomization}
          label="Allow customization"
          caption="When enabled, organization staff can see and configure customization settings for AICA."
        />
      )}
      <Header
        title="Launcher Icon"
        subtitle="Upload a custom icon to replace the default chat bubble on your Community Assistant launcher. The icon will appear on your website's embedded chat widget and in the chat window header."
      />
      {hasIcon ? (
        <Flex direction="column" gap={6} className="w-full">
          <Flex align="center" gap={4}>
            <div className="relative">
              <img
                src={organization.aiChatIconUrl!}
                alt="Current launcher icon"
                className="h-[60px] w-[60px] rounded-full border border-neutral-200 object-cover"
              />
              <div className="absolute -top-1.5 -right-1.5">
                <Button
                  variant="default"
                  size="sm"
                  icon="FaXmark"
                  onClick={handleRemoveIcon}
                  ariaLabel="Remove icon"
                  className="!h-5 !min-h-0 !w-5 !min-w-0 !rounded-full !p-0 shadow-sm"
                />
              </div>
            </div>
          </Flex>
          <IconPreviews iconUrl={organization.aiChatIconUrl!} />
        </Flex>
      ) : (
        <Flex direction="column" gap={3} align="start">
          <Text.BodySmall>
            Recommended: a square image at least 120×120px. PNG or JPG.
          </Text.BodySmall>
          <Form form={form}>
            <FileUpload {...fileUploadProps} accept={accept} />
          </Form>
        </Flex>
      )}
    </Flex>
  )
}

export default React.memo(CustomizationTab)
